import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AnimatePresence, motion } from "framer-motion";

import CustomerService from "../../api/customer";

import { PHONE_HELPER, STRING_NOT_NULL_HELPER } from "../../common/helpersTxt";
import { validatePhone, validateString } from "../../common/validators";

// APP COMPONENTS
import CustomerReport from "./report/CustomerReport";
import CreateOrEditAddress from "../address/CreateOrEditAddress";

// MATERIAL UI COMPONENTS
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoneyIcon from "@mui/icons-material/Money";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Custom Components
import MoneyInput from "../Inputs/MoneyInput";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

// --- Animation ---

const modalVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.97,
    transition: { duration: 0.15 },
  },
};

// --- Skeleton ---

const FormSkeleton = () => (
  <Stack spacing={2.5} sx={{ p: 3 }}>
    {[...Array(4)].map((_, i) => (
      <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1.5 }} />
    ))}
    <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
  </Stack>
);

// --- Component ---

export default function NewCustomerModal({
  show,
  closeModal,
  customer,
  createCustomer,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [success, setSuccess] = useState(false);

  const [customerId, setCustomerId] = useState(
    customer?.customer_id || uuidv4()
  );

  const [hasTyped, setHasTyped] = useState(false);

  // Form fields
  const [name, setName] = useState(customer?.name || "");
  const [isNameValid, setIsNameValid] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState(customer?.phonenmbr || "");
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [email, setEmail] = useState(customer?.email || "");
  const [cpf, setCpf] = useState(customer?.cpf || "");

  const [isBlacklisted, setIsBlacklisted] = useState(
    customer?.is_blacklisted || false
  );

  const [customDeliveryTax, setCustomDeliveryTax] = useState(
    customer?.custom_delivery_tax / 100 || null
  );
  const [customDeliveryGuyDeliveryTax, setCustomDeliveryGuyDeliveryTax] =
    useState(customer?.custom_delivery_guy_delivery_tax / 100 || null);

  // Address state
  const [hasAddress, setHasAddress] = useState(false);
  const [address, setAddress] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Error handling
  const [error, setError] = useState("");

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);

    validateName(name);
    validatePhoneNumber(phoneNumber);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      if (!customer) {
        setLoading(false);
        return;
      }

      const { data } = await CustomerService.customerAddress(
        customer.customer_id
      );
      setAddress(data.length > 0 ? data[0] : null);
      setHasAddress(data.length > 0);
      setIsEditing(true);
      setLoading(false);
    } catch (e) {
      setError("Erro ao carregar dados do cliente");
      dispatchSnackBarMessage(
        `new-or-edit-customer-${Date.now()}`,
        "Erro ao carregar dados do clientes",
        "error"
      );
      setLoading(false);
    }
  };

  const handleShowAddressModal = () => {
    setShowAddressModal(!showAddressModal);
  };

  const validateName = (value) => {
    const isValid = validateString(value);
    setIsNameValid(isValid);
    return isValid;
  };

  const validatePhoneNumber = (value) => {
    const isValid = validatePhone(value);
    setIsPhoneValid(isValid);
    return isValid;
  };

  const handleBlacklistChange = (event) => {
    setIsBlacklisted(event.target.checked);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setHasTyped(true);

    const isNameOk = validateName(name);
    const isPhoneOk = validatePhoneNumber(phoneNumber);

    if (isNameOk || isPhoneOk) {
      try {
        setSaving(true);
        setError("");

        const customerObj = {
          customer_id: isEditing ? customer.customer_id : customerId,
          name: name,
          phonenmbr: phoneNumber,
          email: email,
          cpf: cpf,
          is_blacklisted: isBlacklisted,
          custom_delivery_tax: customDeliveryTax
            ? customDeliveryTax * 100
            : null,
          custom_delivery_guy_delivery_tax: customDeliveryGuyDeliveryTax
            ? customDeliveryGuyDeliveryTax * 100
            : null,
        };

        await createCustomer(isEditing, customerObj);

        setSuccess(true);
        setTimeout(() => {
          closeModal();
        }, 1000);
      } catch (err) {
        setError("Erro ao salvar cliente. Por favor, tente novamente.");
      } finally {
        setSaving(false);
      }
    } else {
      setError("Por favor, preencha os campos obrigatórios corretamente.");
    }
  };

  // --- Address inline preview ---
  const renderAddressPreview = () => {
    if (!hasAddress || !address) {
      return (
        <Box
          onClick={handleShowAddressModal}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.grey[300]}`,
            bgcolor: theme.palette.grey[50],
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <AddCircleOutlineIcon
            sx={{ color: theme.palette.text.disabled, fontSize: 20 }}
          />
          <Typography variant="body2" color="text.secondary">
            Adicionar endereço de entrega
          </Typography>
        </Box>
      );
    }

    const addressLine = [
      address.street,
      address.house_number,
      address.address_complement,
    ]
      .filter(Boolean)
      .join(", ");

    const cityLine = [
      address.neighborhood,
      `${address.city}/${address.uf}`,
      address.cep ? `CEP ${address.cep}` : null,
    ]
      .filter(Boolean)
      .join(" - ");

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <HomeIcon
          sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.25 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {addressLine}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {cityLine}
          </Typography>
          {address.address_reference && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              Ref: {address.address_reference}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
            {address.lat && address.long && (
              <Chip
                icon={<LocationOnIcon sx={{ fontSize: "14px !important" }} />}
                label="Geolocalização"
                color="success"
                size="small"
                sx={{ height: 20, fontSize: "0.7rem", borderRadius: 1 }}
              />
            )}
          </Stack>
        </Box>
        <IconButton
          size="small"
          onClick={handleShowAddressModal}
          sx={{
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <Modal
        aria-labelledby="customer-modal-title"
        open={show}
        onClose={closeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
        sx={{
          zIndex: 5000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={show}>
          <Box
            component={motion.div}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            sx={{
              position: "relative",
              width: { xs: "95%", sm: 560 },
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              bgcolor: "background.paper",
              boxShadow: 24,
              overflow: "hidden",
              "&:focus-visible": { outline: "none" },
            }}
          >
            {/* ── Header ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: 1.5,
                    width: 36,
                    height: 36,
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} id="customer-modal-title">
                    {customer ? "Editar Cliente" : "Novo Cliente"}
                  </Typography>
                  {customer && (
                    <Typography variant="caption" color="text.secondary">
                      {customer.name}
                    </Typography>
                  )}
                </Box>
                {isBlacklisted && (
                  <Chip
                    size="small"
                    label="Bloqueado"
                    color="error"
                    variant="filled"
                    icon={<BlockIcon fontSize="small" />}
                    sx={{ height: 22 }}
                  />
                )}
              </Stack>

              <IconButton
                aria-label="Fechar modal"
                onClick={closeModal}
                size="small"
                sx={{
                  color: theme.palette.grey[400],
                  "&:hover": { color: theme.palette.grey[600] },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* ── Alerts ── */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ flexShrink: 0 }}
                >
                  <Alert
                    severity="error"
                    variant="standard"
                    icon={<ErrorOutlineIcon />}
                    action={
                      <IconButton
                        aria-label="Fechar alerta"
                        color="inherit"
                        size="small"
                        onClick={() => setError("")}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{
                      mx: 2,
                      mt: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    }}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}
              {success && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ flexShrink: 0 }}
                >
                  <Alert
                    severity="success"
                    variant="standard"
                    icon={<CheckCircleIcon />}
                    sx={{
                      mx: 2,
                      mt: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  >
                    Cliente salvo com sucesso!
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Content (scrollable, single column) ── */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {loading ? (
                <FormSkeleton />
              ) : (
                <Stack spacing={0} sx={{ p: 2 }}>
                  {/* ── Section 1: Core Fields ── */}
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          variant="outlined"
                          id="customer-name"
                          label="Nome"
                          size="small"
                          fullWidth
                          required
                          error={!isNameValid && submitted}
                          value={name}
                          helperText={
                            !isNameValid && submitted ? STRING_NOT_NULL_HELPER : ""
                          }
                          onChange={(event) => {
                            const value = event.target.value;
                            setName(value);
                            validateName(value);
                            setHasTyped(true);
                          }}
                          InputProps={{
                            startAdornment: (
                              <PersonIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          variant="outlined"
                          id="customer-phone"
                          label="Telefone"
                          size="small"
                          fullWidth
                          required
                          error={!isPhoneValid && hasTyped}
                          value={phoneNumber}
                          helperText={!isPhoneValid && hasTyped ? PHONE_HELPER : ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setPhoneNumber(value);
                            validatePhoneNumber(value);
                            setHasTyped(true);
                          }}
                          InputProps={{
                            startAdornment: (
                              <PhoneIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            ),
                          }}
                          inputProps={{ maxLength: 11 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          variant="outlined"
                          id="customer-cpf"
                          label="CPF"
                          size="small"
                          fullWidth
                          value={cpf}
                          onChange={(event) => setCpf(event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <BadgeIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          variant="outlined"
                          id="customer-email"
                          label="E-mail"
                          size="small"
                          fullWidth
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <EmailIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>

                  {/* ── Section 2: Address (compact inline) ── */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      Endereço de Entrega
                    </Typography>
                    {renderAddressPreview()}
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* ── Section 3: Blacklist Toggle (compact) ── */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BlockIcon
                        fontSize="small"
                        sx={{
                          color: isBlacklisted
                            ? theme.palette.error.main
                            : theme.palette.text.disabled,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Lista de Não Confiáveis
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isBlacklisted
                            ? "Cliente bloqueado"
                            : "Cliente normal"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Switch
                      checked={isBlacklisted}
                      onChange={handleBlacklistChange}
                      color="error"
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* ── Section 4: Custom Taxes (collapsible) ── */}
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      "&:before": { display: "none" },
                      bgcolor: "transparent",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 0,
                        minHeight: 48,
                        "& .MuiAccordionSummary-content": { my: 0 },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MoneyIcon
                          fontSize="small"
                          sx={{ color: theme.palette.success.main }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          Taxas Personalizadas
                        </Typography>
                        {(customDeliveryTax || customDeliveryGuyDeliveryTax) && (
                          <Chip
                            label="Ativo"
                            size="small"
                            color="success"
                            sx={{ height: 18, fontSize: "0.65rem", borderRadius: 1 }}
                          />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                      <Stack spacing={2}>
                        <MoneyInput
                          label="Taxa de Entrega Fixa"
                          value={customDeliveryTax}
                          setValue={setCustomDeliveryTax}
                        />
                        <MoneyInput
                          label="Taxa de Entrega Fixa Entregador"
                          value={customDeliveryGuyDeliveryTax}
                          setValue={setCustomDeliveryGuyDeliveryTax}
                        />
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* ── Section 5: Order History (collapsible, edit only) ── */}
                  {customer && (
                    <>
                      <Divider />
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                          "&:before": { display: "none" },
                          bgcolor: "transparent",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: 0,
                            minHeight: 48,
                            "& .MuiAccordionSummary-content": { my: 0 },
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocalShippingIcon
                              fontSize="small"
                              sx={{ color: theme.palette.primary.main }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              Histórico de Pedidos
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                          <CustomerReport customerId={customer.customer_id} />
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}
                </Stack>
              )}
            </Box>

            {/* ── Sticky Footer ── */}
            {!loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  px: 3,
                  py: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={closeModal}
                  disabled={saving}
                  size="small"
                  sx={{ borderRadius: 2, px: 2.5 }}
                >
                  Cancelar
                </Button>

                <Button
                  variant="contained"
                  color={success ? "success" : "primary"}
                  onClick={handleSubmit}
                  disabled={saving || success}
                  startIcon={success ? <CheckCircleIcon /> : <SaveIcon />}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    px: 2.5,
                    position: "relative",
                    minWidth: 140,
                  }}
                >
                  {saving
                    ? "Salvando..."
                    : success
                    ? "Salvo!"
                    : isEditing
                    ? "Salvar Alterações"
                    : "Cadastrar Cliente"}

                  {saving && (
                    <CircularProgress
                      size={20}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-10px",
                        marginLeft: "-10px",
                      }}
                    />
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {showAddressModal && (
        <CreateOrEditAddress
          show={showAddressModal}
          address={address}
          closeModal={handleShowAddressModal}
          updateInfos={loadData}
          customer_id={customerId}
        />
      )}
    </>
  );
}
